<template>
	<div>
		<TemplateSelection
			:templates="templates"
		/>

		<CStatusButton
			class=" button button--primary text-4"
			title="Select Templates"
			type="submit"
			:status="continueButtonStatus"
			@click.native="selectTemplates"
		>
			Continue
		</CStatusButton>
	</div>
</template>

<script lang="ts">
	import { defineComponent } from 'vue';
	import CStatusButton from 'src/components/interaction/StatusButton.vue';
	import TemplateSelection from 'src/views/TemplateSelection.vue';
	import { getAllRelatedForms, copyForm } from '@cognitoforms/api/services/form-service';
	import SessionConsumer from 'src/mixins/session-consumer';
	import urlize from '@cognitoforms/utils/urlize';

	export default defineComponent({
		name: 'SelectTemplates',
		components: {
			CStatusButton,
			TemplateSelection
		},
		mixins: [SessionConsumer],
		props: {
			organizationName: {
				type: String,
				default: ''
			},
			selectedJob: {
				type: String,
				default: ''
			},
			selectedRole: {
				type: String,
				default: ''
			},
			selectedIndustry: {
				type: String,
				default: ''
			},
			siteManifest: {
				type: Object,
				default: null
			}
		},
		data() {
			return {
				templates: null,
				continueButtonStatus: 'default'
			};
		},
		head() {
			return {
				title: 'Select Templates',
				meta: [
					{
						name: 'description',
						content: 'Cognito Forms, a free online form builder that helps you collect information and payments. Easily create feedback forms, payment forms, registration forms, and much more.'
					}
				],
				htmlAttrs: {
					class: [
						'select-templates-page'
					]
				}
			};
		},
		mounted() {
			if (this.siteManifest) {
				const industry = this.siteManifest.industries.list.all.find(i => i.cfid === this.selectedIndustry);
				// We urlize the title here as the industries slug is pulled from the canonicalUrl, not the title
				const industrySlug = urlize(industry?.title);

				// Grab ranked templates for industry selected.
				this.templates = this.siteManifest.templates.list.byRanking[industrySlug];

				// Order by ranking and grab first 9
				this.templates = this.templates?.sort((a, b) => {
					const aRanking = a.rankings.find(r => r.slug === industrySlug).rank;
					const bRanking = b.rankings.find(r => r.slug === industrySlug).rank;

					return aRanking > bRanking ? 1 : -1;
				}).slice(0, 9);

				// if industry does not have ranked templates, show first 9 alphabetically
				if (!this.templates)
					this.templates = this.siteManifest.templates.list.byTag[industry?.identifier]?.slice(0, 9);

				// Fall back to all templates if industry does not have tagged templates. This should not happen
				if (!this.templates)
					this.templates = this.siteManifest.templates.list.all.slice(0, 9);

				// Set first three templates to selected automatically
				this.templates.slice(0, 3).forEach(t => { t.selected = true; });
			}
		},
		methods: {
			async selectTemplates() {
				this.continueButtonStatus = 'validating';
				const selectedTemplates = this.templates.filter(t => t.selected);

				if (selectedTemplates.length === 0) {
					// Continue without copying templates
					this.$emit('done');
					this.continueButtonStatus = 'default';
				}
				else {
					// Copy templates to org
					const organizationId = this.session?.value?.organizationId;
					const templateOrgId = 'cognitotemplates'; // Cognito Templates org in production
					let copiedGroups = 0;

					selectedTemplates.forEach(t => {
						t.FormId = t.form ?? t.cfid;
					});

					const formsToCopy = await getAllRelatedForms(templateOrgId, selectedTemplates, true);

					formsToCopy.forEach(group => {
						// Set CopyEntries to true if form has entries available
						group.forEach(form => {
							if (form.TotalEntries > 0)
								form.CopyEntries = true;
						});

						copyForm(templateOrgId, organizationId, null, 'Template', group).then((res) => {
							copiedGroups++;

							// If last template selected, emit to redirect to dashboard
							if (copiedGroups === formsToCopy.length)
								this.$emit('done');
						});
					});
				}
			}
		}
	});
</script>
<style lang="scss">
:root:root:root.select-templates-page {

	.faux-modal__body {
		position: relative;
		z-index: 0;
		max-width: 1000px;
		padding-right: min(60px, 6vw);
		padding-left: min(60px, 6vw);
	}

	.faux-modal__wrapper {
		position: relative;
		z-index: 0;
		max-width: initial;
	}

	.button {
		display: inline-block !important;
		width: 300px;
	}
}

</style>
